import {useCallback, useEffect, useRef, useState} from 'react'

export type VoiceInputStatus = 'idle' | 'connecting' | 'recording' | 'stopping'

interface UseVoiceInputOptions {
  /** Function that fetches a temporary AssemblyAI streaming token from the backend */
  fetchToken: () => Promise<string>
  onTranscribed: (text: string) => void
  onError?: (message: string) => void
}

interface UseVoiceInputReturn {
  status: VoiceInputStatus
  transcript: string
  /** Start or stop recording. When stopping, returns the final transcribed text. */
  toggle: () => string
}

// AssemblyAI v3 Streaming Speech-to-Text WebSocket endpoint
const ASSEMBLYAI_STREAMING_URL = 'wss://streaming.assemblyai.com/v3/ws'
const SAMPLE_RATE = 16_000
const SCRIPT_PROCESSOR_BUFFER = 4096

/**
 * Downsample a Float32Array from the source sample rate to the target sample rate
 * using linear interpolation, then convert to Int16 PCM.
 */
function downsampleToInt16(buffer: Float32Array, inputRate: number, outputRate: number): ArrayBuffer {
  if (inputRate === outputRate) {
    const int16 = new Int16Array(buffer.length)
    for (let i = 0; i < buffer.length; i++) {
      const s = Math.max(-1, Math.min(1, buffer[i]))
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
    }
    return int16.buffer
  }

  const ratio = inputRate / outputRate
  const outputLength = Math.round(buffer.length / ratio)
  const int16 = new Int16Array(outputLength)

  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio
    const srcIndexFloor = Math.floor(srcIndex)
    const srcIndexCeil = Math.min(srcIndexFloor + 1, buffer.length - 1)
    const fraction = srcIndex - srcIndexFloor

    const sample = buffer[srcIndexFloor] + fraction * (buffer[srcIndexCeil] - buffer[srcIndexFloor])
    const clamped = Math.max(-1, Math.min(1, sample))
    int16[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7FFF
  }

  return int16.buffer
}

export function useVoiceInput({fetchToken, onTranscribed, onError}: UseVoiceInputOptions): UseVoiceInputReturn {
  const [status, setStatus] = useState<VoiceInputStatus>('idle')
  const [transcript, setTranscript] = useState('')

  const socketRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const finalTextRef = useRef('')
  const statusRef = useRef<VoiceInputStatus>('idle')

  useEffect(() => {
    statusRef.current = status
  }, [status])

  const cleanup = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect()
      processorRef.current.onaudioprocess = null
      processorRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (socketRef.current) {
      if (socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({type: 'Terminate'}))
      }
      socketRef.current.close()
      socketRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      cleanup()
    }
  }, [cleanup])

  const start = useCallback(async () => {
    if (statusRef.current !== 'idle') return
    setStatus('connecting')
    setTranscript('')
    finalTextRef.current = ''

    let token: string
    try {
      token = await fetchToken()
    } catch (err: any) {
      console.error('[useVoiceInput] Token fetch failed:', err)
      setStatus('idle')
      onError?.(err.message || 'Failed to get transcription token')
      return
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      })
      streamRef.current = stream
    } catch (err: any) {
      console.error('[useVoiceInput] Microphone error:', err)
      setStatus('idle')
      if (err.name === 'NotAllowedError') {
        onError?.('Microphone permission denied. Please allow microphone access and try again.')
      } else {
        onError?.('Could not access microphone')
      }
      return
    }

    // v3 Streaming API — token passed as query param (browser WebSocket can't set headers)
    const wsUrl = `${ASSEMBLYAI_STREAMING_URL}?token=${encodeURIComponent(token)}&sample_rate=${SAMPLE_RATE}`
    const socket = new WebSocket(wsUrl)
    socket.binaryType = 'arraybuffer'
    socketRef.current = socket

    socket.onopen = () => {
      const audioContext = new AudioContext()
      audioContextRef.current = audioContext
      const source = audioContext.createMediaStreamSource(stream)
      const processor = audioContext.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER, 1, 1)
      processorRef.current = processor

      processor.onaudioprocess = (event) => {
        if (socketRef.current?.readyState !== WebSocket.OPEN) return
        const inputData = event.inputBuffer.getChannelData(0)
        const pcm16 = downsampleToInt16(inputData, audioContext.sampleRate, SAMPLE_RATE)
        socketRef.current.send(pcm16)
      }

      source.connect(processor)
      processor.connect(audioContext.destination)
      setStatus('recording')
    }

    socket.onmessage = (event) => {
      let message: any
      try {
        message = JSON.parse(event.data)
      } catch {
        return
      }

      if (message.error) {
        console.error('[useVoiceInput] AssemblyAI error:', message.error)
        return
      }

      // v3 Streaming API uses Turn events with end_of_turn flag
      if (message.type === 'Turn' && message.transcript) {
        if (message.end_of_turn) {
          // Final turn — append to accumulated text
          finalTextRef.current += (finalTextRef.current ? ' ' : '') + message.transcript
          setTranscript(finalTextRef.current)
        } else {
          // Partial turn — show accumulated + current partial
          setTranscript(finalTextRef.current + (finalTextRef.current ? ' ' : '') + message.transcript)
        }
      }
    }

    socket.onerror = () => {
      console.error('[useVoiceInput] WebSocket error')
      cleanup()
      setStatus('idle')
      setTranscript('')
      onError?.('Voice transcription connection failed')
    }

    socket.onclose = () => {
      if (statusRef.current === 'recording') {
        console.warn('[useVoiceInput] WebSocket closed unexpectedly')
        cleanup()
        const text = finalTextRef.current.trim()
        if (text) {
          onTranscribed(text)
        }
        setStatus('idle')
        setTranscript('')
      }
    }
  }, [fetchToken, onError, cleanup, onTranscribed])

  const stop = useCallback((): string => {
    if (statusRef.current !== 'recording') return ''
    setStatus('stopping')
    cleanup()

    const text = finalTextRef.current.trim()
    setStatus('idle')
    setTranscript('')
    return text
  }, [cleanup])

  const toggle = useCallback((): string => {
    if (statusRef.current === 'idle') {
      start()
      return ''
    } else if (statusRef.current === 'recording') {
      return stop()
    }
    return ''
  }, [start, stop])

  return {status, transcript, toggle}
}
